/**
 * Firebase Auth and Storage for Passport app.
 * Config from generated-env.ts (from Passport/.env via scripts/generate-env.cjs).
 *
 * Phase 4 (client failover): the active Firebase project (primary | secondary) is
 * resolved at bootstrap from /runtime-config.json (see loadPassportConfig.ts).
 * Init is lazy (getFirebaseAuth), which runs AFTER loadPassportConfig(), so the
 * selector is known. "secondary" is honored only when a complete secondary config
 * is bundled (VITE_FIREBASE_*_SECONDARY); otherwise the app stays on primary.
 */
import { initializeApp } from 'firebase/app';
import { getAuth, type Auth } from 'firebase/auth';
import { getStorage, type FirebaseStorage } from 'firebase/storage';
import {
  VITE_FIREBASE_API_KEY,
  VITE_FIREBASE_AUTH_DOMAIN,
  VITE_FIREBASE_PROJECT_ID,
  VITE_FIREBASE_STORAGE_BUCKET,
  VITE_FIREBASE_MESSAGING_SENDER_ID,
  VITE_FIREBASE_APP_ID,
  VITE_FIREBASE_API_KEY_SECONDARY,
  VITE_FIREBASE_AUTH_DOMAIN_SECONDARY,
  VITE_FIREBASE_PROJECT_ID_SECONDARY,
  VITE_FIREBASE_STORAGE_BUCKET_SECONDARY,
  VITE_FIREBASE_MESSAGING_SENDER_ID_SECONDARY,
  VITE_FIREBASE_APP_ID_SECONDARY
} from '../generated-env';
import { getActiveFirebaseProject } from '../loadPassportConfig';
import { initFirebaseAppCheck } from '../utils/firebaseAppCheck';

function str(v: unknown): string {
  return typeof v === 'string' ? v.trim() : '';
}

interface PassportFirebaseConfig {
  apiKey: string;
  authDomain: string;
  projectId: string;
  storageBucket: string;
  messagingSenderId: string;
  appId: string;
}

const primaryConfig: PassportFirebaseConfig = {
  apiKey: str(VITE_FIREBASE_API_KEY),
  authDomain: str(VITE_FIREBASE_AUTH_DOMAIN),
  projectId: str(VITE_FIREBASE_PROJECT_ID),
  storageBucket: str(VITE_FIREBASE_STORAGE_BUCKET),
  messagingSenderId: str(VITE_FIREBASE_MESSAGING_SENDER_ID),
  appId: str(VITE_FIREBASE_APP_ID)
};

const secondaryConfig: PassportFirebaseConfig = {
  apiKey: str(VITE_FIREBASE_API_KEY_SECONDARY),
  authDomain: str(VITE_FIREBASE_AUTH_DOMAIN_SECONDARY),
  projectId: str(VITE_FIREBASE_PROJECT_ID_SECONDARY),
  storageBucket: str(VITE_FIREBASE_STORAGE_BUCKET_SECONDARY),
  messagingSenderId: str(VITE_FIREBASE_MESSAGING_SENDER_ID_SECONDARY),
  appId: str(VITE_FIREBASE_APP_ID_SECONDARY)
};

function isComplete(c: PassportFirebaseConfig): boolean {
  return !!(c.apiKey && c.projectId);
}

const secondaryConfigIsComplete = !!(
  secondaryConfig.apiKey &&
  secondaryConfig.authDomain &&
  secondaryConfig.projectId &&
  secondaryConfig.storageBucket &&
  secondaryConfig.messagingSenderId &&
  secondaryConfig.appId
);

/** Resolve the config to initialize against, honoring the DR cutover selector. */
function getActiveConfig(): PassportFirebaseConfig {
  if (getActiveFirebaseProject() === 'secondary' && secondaryConfigIsComplete) {
    return secondaryConfig;
  }
  return primaryConfig;
}

export function hasFirebaseConfig(): boolean {
  const has = isComplete(getActiveConfig());
  if (import.meta.env.DEV && !has) {
    console.warn(
      '[Passport] Firebase config missing. Save Passport/.env with VITE_FIREBASE_API_KEY and VITE_FIREBASE_PROJECT_ID, then run: npm run generate-env && npm run dev'
    );
  }
  return has;
}

let app: ReturnType<typeof initializeApp>;
let auth: Auth | null = null;
let storage: FirebaseStorage | null = null;

export function getFirebaseAuth(): Auth {
  if (auth) return auth;
  const firebaseConfig = getActiveConfig();
  if (!firebaseConfig.apiKey || !firebaseConfig.projectId) {
    throw new Error(
      'Missing Firebase config. Run: npm run generate-env (after saving Passport/.env with VITE_FIREBASE_* from Firebase Console).'
    );
  }
  app = initializeApp(firebaseConfig);
  initFirebaseAppCheck(app);
  auth = getAuth(app);
  return auth;
}

/** Firebase Storage for Passport VVID uploads (Phase 6). Path: carrierPassports/{passportId}/vvid/ or .../secure-documents/ */
export function getFirebaseStorage(): FirebaseStorage {
  if (storage) return storage;
  getFirebaseAuth(); // ensure app initialized
  storage = getStorage(app!);
  return storage;
}
